import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Divider, useTheme } from '@mui/material'
import { Box, Card, Collapse, IconButton, Stack, Typography } from '@mui/material'
import { useRouteMatch, useHistory, useLocation } from 'react-router-dom'
import PropertyFlood from '../components/climateAnalytics/property/PropertyFlood'
import Page from '../components/Page'
import { useClimateHazards } from '../hooks/analytics'
import { useParams } from 'react-router-dom'
import LoadingIcon from '../components/LoadingIcon'
import { Alert } from '@mui/material'
import { CanonicalPeriod, EmissionTier, AdaptationView } from '../components/climateAnalytics/common'
import PropertyWildfire from '../components/climateAnalytics/property/PropertyWildfire'
import PropertyAdaptationsCard from '../components/climateAnalytics/property/PropertyAdaptationsCard'
import PropertyHeatStress from '../components/climateAnalytics/property/PropertyHeatStress'
import PropertyExtremeWind from '../components/climateAnalytics/property/PropertyExtremeWind'
import PropertyWaterRisk from '../components/climateAnalytics/property/PropertyWaterRisk'
import siteMap from '../siteMap'
import EmissionTierSelector from '../components/climateAnalytics/common/EmissionTierSelector'
import HorizonSelector from '../components/climateAnalytics/common/HorizonSelector'
import HazardSelector from '../components/climateAnalytics/common/HazardSelector'
import AdaptationViewSelector from '../components/climateAnalytics/common/AdaptationViewSelector'
import ClimateContextBar from '../components/climateAnalytics/common/ClimateContextBar'
import ClimateMetricStateNotice from '../components/climateAnalytics/common/ClimateMetricStateNotice'
import { useClimateMetricStates } from '../hooks/climateData/useClimateMetricStates'
import { useLocation as useProperty } from '../hooks/locations'
import { usePropertyAdaptations } from '../hooks/climateAnalytics'
import { useCurrentUser } from '../hooks/users'
import Map, { MapRef, Marker } from 'react-map-gl'
import ConfigProvider from 'balkerne-core/config'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { useDispatch, useSelector } from 'react-redux'
import systemActions from '../store/system'
import { RootState } from '../store'
import HazardScenarioPreferenceControl from '../components/climateAnalytics/common/HazardScenarioPreferenceControl'
import { useHazardScenarioPreferences } from '../components/climateAnalytics/common/useHazardScenarioPreferences'
import { useClimateAnalyticsRouteState } from '../components/climateAnalytics/common/useClimateAnalyticsRouteState'
import MethodologyIconButton from '../components/methodology/MethodologyIconButton'

const PropertyClimateAnalytics = () => {
  const theme = useTheme()
  const history = useHistory()
  const dispatch = useDispatch()
  const location = useLocation()
  const hazardMatch = useRouteMatch<{ hazard?: string }>(`${siteMap.PropertyClimateAnalytics.path}/:hazard`)
  const { id: propertyId } = useParams<{ id: string }>()
  const numericPropertyId = useMemo(() => {
    const parsed = Number(propertyId)
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null
  }, [propertyId])
  const { data: property, isLoading: isPropertyLoading } = useProperty(propertyId)
  const { data: hazards, isLoading: isHazardsLoading } = useClimateHazards()
  const authUser = useSelector((state: RootState) => state.auth.user)
  const { data: currentUser, currencyISO } = useCurrentUser()

  const navigation = useMemo(
    () => ({
      push: (path: string) => history.push(path),
      replace: (path: string) => history.replace(path),
    }),
    [history],
  )
  const buildPropertyClimateAnalyticsPath = useCallback(
    (hazardCode: string, nextEmissionTier: EmissionTier, nextPeriod: CanonicalPeriod, nextView: AdaptationView) =>
      siteMap.PropertyClimateAnalytics.getPath(propertyId, hazardCode, nextEmissionTier, nextPeriod, nextView),
    [propertyId],
  )
  const {
    emissionTier,
    adaptationView,
    handleAdaptationViewChange,
    handleEmissionTierChange,
    handleHazardChange,
    handlePeriodChange,
    hazardViewConfig: selectedHazardViewConfig,
    selectedHazardCode,
    selectedPeriod,
  } = useClimateAnalyticsRouteState({
    hazards,
    hazardFromRoute: hazardMatch?.params?.hazard,
    pathname: location.pathname,
    search: location.search,
    navigation,
    buildPath: buildPropertyClimateAnalyticsPath,
  })
  const hazardScenarioPreferences = useHazardScenarioPreferences(
    currentUser ?? authUser,
    selectedHazardCode,
    selectedHazardViewConfig.scenarioPreferences,
  )
  const mapRef = useRef<MapRef | null>(null)
  const [showPropertyDetails, setShowPropertyDetails] = useState(false)

  useEffect(() => {
    if (numericPropertyId != null) {
      dispatch(systemActions.setPropertyId(numericPropertyId))
    }
  }, [dispatch, numericPropertyId])

  const propertyAddress = useMemo(() => {
    const parts = [
      property?.address?.street,
      property?.address?.town,
      property?.address?.postcode,
      property?.address?.country,
    ].filter(Boolean)
    return parts.join(', ')
  }, [property])

  const financialMetrics = useMemo(() => {
    if (!property?.insurance) return []
    const currency = currencyISO || 'GBP'
    const formatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    })

    return [
      { label: 'Structure value', value: property.insurance.structure },
      { label: 'Contents value', value: property.insurance.contents },
      { label: 'Revenue', value: property.insurance.revenue },
    ]
      .filter(metric => metric.value != null)
      .map(metric => ({
        label: metric.label,
        value: formatter.format(Number(metric.value)),
      }))
  }, [currencyISO, property])

  const togglePropertyDetails = useCallback(() => {
    setShowPropertyDetails(prev => !prev)
  }, [])

  const handlePropertyDetailsKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      togglePropertyDetails()
    },
    [togglePropertyDetails],
  )

  const mapViewState = useMemo(
    () => ({
      longitude: property?.coordinates?.longitude ?? -0.1276,
      latitude: property?.coordinates?.latitude ?? 51.5072,
      zoom: property?.coordinates ? 12 : 2,
    }),
    [property],
  )

  const resetMapView = useCallback(() => {
    const map = mapRef.current?.getMap()
    if (!map) return
    map.stop()
    map.jumpTo({
      center: [mapViewState.longitude, mapViewState.latitude],
      zoom: mapViewState.zoom,
      bearing: 0,
      pitch: 0,
    })
  }, [mapViewState])

  useEffect(() => {
    resetMapView()
  }, [resetMapView])

  // property: object (example)
  // {
  //     "id": 9635,
  //     "organisation_id": 1,
  //     "name": "82 Manchester Road",
  //     "type": {
  //         "id": "196c16a3-cc9d-5f28-90f2-c0272fe6a352",
  //         "is_system": true,
  //         "label": "Unknown",
  //         "created_at": "2025-11-30T21:39:24.920771"
  //     },
  //     "property_customer_reference": "3245b097-c506-4d60-8615-6264f1c14e2b",
  //     "coordinates": {
  //         "latitude": 53.58436225297437,
  //         "longitude": -2.210266880526774
  //     },
  //     "created_at": "2025-11-30T21:39:25.206944",
  //     "geometry": null,
  //     "address": {
  //         "country": "England",
  //         "town": "Heywood",
  //         "postcode": "OL10 2PN",
  //         "street": "Manchester Road"
  //     },
  //     "attributes": [],
  //     "groups": [],
  //     "insurance": {
  //         "structure": 3000000,
  //         "contents": null,
  //         "revenue": null
  //     },
  //     "metadata": {
  //         "distance": null
  //     }
  // }
  const selectHazard = useMemo(() => {
    return hazards?.find(h => h.name === selectedHazardCode)
  }, [hazards, selectedHazardCode])

  const adaptationsHazardId = selectedHazardViewConfig.supportsAdaptations ? selectHazard?.id : undefined
  const { data: propertyAdaptations } = usePropertyAdaptations(numericPropertyId ?? 0, adaptationsHazardId)
  const hasAdaptations = (propertyAdaptations?.length ?? 0) > 0

  const metricStatesSummary = useClimateMetricStates({
    type: 'property',
    propertyId: numericPropertyId ?? -1,
    hazardCode: selectedHazardCode,
  })

  const renderHazardComponent = useMemo(() => {
    if (numericPropertyId == null) {
      return <Alert severity="info">Select a property to view climate analytics.</Alert>
    }

    switch (selectedHazardCode) {
      case 'fld':
        return (
          <PropertyFlood
            emissionTier={emissionTier}
            propertyId={numericPropertyId}
            selectedPeriod={selectedPeriod}
            view={adaptationView}
            filters={hazardScenarioPreferences.filters}
          />
        )
      case 'wf':
        return (
          <PropertyWildfire
            emissionTier={emissionTier}
            propertyId={numericPropertyId}
            selectedPeriod={selectedPeriod}
            view={adaptationView}
          />
        )
      case 'hs':
        return (
          <PropertyHeatStress
            emissionTier={emissionTier}
            propertyId={numericPropertyId}
            selectedPeriod={selectedPeriod}
          />
        )
      case 'tc':
        return <PropertyExtremeWind emissionTier={emissionTier} propertyId={numericPropertyId} view={adaptationView} />
      case 'wr':
        return (
          <PropertyWaterRisk
            emissionTier={emissionTier}
            propertyId={numericPropertyId}
            selectedPeriod={selectedPeriod}
          />
        )
      default:
        return null
    }
  }, [
    selectedHazardCode,
    emissionTier,
    adaptationView,
    hazardScenarioPreferences.filters,
    numericPropertyId,
    selectedPeriod,
  ])

  if (isHazardsLoading) {
    return (
      <Page size="lg" title="Climate Analytics" back>
        <Stack
          flexGrow={1}
          justifyContent="center"
          alignItems="center"
          sx={{
            height: '200px',
            width: '100%',
          }}>
          <LoadingIcon />
        </Stack>
      </Page>
    )
  }

  return (
    <Page size="lg" title="Climate Analytics" back>
      <Stack direction="column" spacing={3}>
        {selectedHazardCode == null ? (
          <Stack
            flexGrow={1}
            justifyContent="center"
            alignItems="center"
            sx={{
              height: '100px',
              width: '100%',
            }}>
            <Typography variant="h6">Select a hazard to view analytics</Typography>
          </Stack>
        ) : (
          <>
            <Stack spacing={1.5}>
              <Box sx={{ minWidth: 0, maxWidth: 760 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h4">{selectHazard?.display_name ?? 'Hazard'}</Typography>
                  {selectedHazardCode && <MethodologyIconButton hazardCode={selectedHazardCode} />}
                </Stack>
                {selectHazard?.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    {selectHazard.description}
                  </Typography>
                )}
              </Box>

              <ClimateContextBar
                hazardControl={
                  <HazardSelector
                    hazards={hazards}
                    value={selectedHazardCode ?? ''}
                    onChange={handleHazardChange}
                    selectedAction={
                      selectedHazardViewConfig.scenarioPreferences ? (
                        <HazardScenarioPreferenceControl
                          filters={hazardScenarioPreferences.filters}
                          hazardCode={selectedHazardCode}
                          hazardLabel={selectHazard?.display_name ?? 'Hazard'}
                          config={selectedHazardViewConfig.scenarioPreferences}
                          onSave={hazardScenarioPreferences.saveFilters}
                        />
                      ) : undefined
                    }
                  />
                }
                emissionControl={<EmissionTierSelector value={emissionTier} onChange={handleEmissionTierChange} />}
                horizonControl={
                  <HorizonSelector
                    value={selectedPeriod}
                    availablePeriods={selectedHazardViewConfig.availablePeriods}
                    onChange={handlePeriodChange}
                  />
                }
                adaptationControl={
                  selectedHazardViewConfig.supportsAdaptations ? (
                    <AdaptationViewSelector
                      value={adaptationView}
                      onChange={handleAdaptationViewChange}
                      disabled={!hasAdaptations}
                      helpText={
                        hasAdaptations
                          ? undefined
                          : 'No adaptations are installed for this property, so effective and baseline results are identical.'
                      }
                    />
                  ) : undefined
                }
              />
            </Stack>

            <ClimateMetricStateNotice summary={metricStatesSummary} />

            <Box
              sx={{
                height: '100%',
                width: '100%',
                borderRadius: 1,
                backgroundColor: theme.palette.background.default,
              }}>
              <Card variant="outlined">
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  role="button"
                  tabIndex={0}
                  aria-expanded={showPropertyDetails}
                  onClick={togglePropertyDetails}
                  onKeyDown={handlePropertyDetailsKeyDown}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    outline: 'none',
                    '&:focus-visible': {
                      boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
                    },
                  }}>
                  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                      Property
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                      {property?.name ?? 'Property'}
                    </Typography>
                  </Stack>
                  <IconButton
                    aria-label="Toggle property details"
                    aria-expanded={showPropertyDetails}
                    onClick={event => {
                      event.stopPropagation()
                      togglePropertyDetails()
                    }}
                    onKeyDown={event => {
                      event.stopPropagation()
                    }}
                    size="small">
                    {showPropertyDetails ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Stack>
                <Collapse in={showPropertyDetails} timeout="auto" unmountOnExit>
                  <Stack spacing={1.5} sx={{ px: 2, pb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Type: {property?.type?.label ?? 'Unknown'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Address: {propertyAddress || 'Unavailable'}
                    </Typography>
                    {financialMetrics.length > 0 && (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
                        {financialMetrics.map(metric => (
                          <Box key={metric.label} sx={{ minWidth: 150 }}>
                            <Typography variant="caption" color="text.secondary">
                              {metric.label}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {metric.value}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    )}
                    {property?.coordinates ? (
                      <Box sx={{ height: 200, borderRadius: 1, overflow: 'hidden' }}>
                        <Map
                          ref={mapRef}
                          reuseMaps
                          initialViewState={mapViewState}
                          mapStyle="mapbox://styles/mapbox/streets-v11"
                          style={{ width: '100%', height: '100%' }}
                          mapboxAccessToken={ConfigProvider.mapboxPublicKey}
                          onLoad={resetMapView}
                          scrollZoom
                          doubleClickZoom
                          dragPan
                          dragRotate
                          keyboard>
                          <Marker
                            longitude={property.coordinates.longitude}
                            latitude={property.coordinates.latitude}
                            anchor="bottom">
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                backgroundColor: theme.palette.primary.main,
                                borderRadius: '50%',
                                border: `2px solid ${theme.palette.background.paper}`,
                              }}
                            />
                          </Marker>
                        </Map>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          height: 200,
                          borderRadius: 1,
                          backgroundColor: theme.palette.background.default,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                        <Typography variant="body2" color="text.secondary">
                          {isPropertyLoading ? 'Loading location...' : 'No coordinates available'}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Collapse>
              </Card>
            </Box>

            <Divider />

            <Box
              sx={{
                height: '100%',
                width: '100%',
                borderRadius: 1,
                backgroundColor: theme.palette.background.default,
              }}>
              <Stack spacing={4}>
                {hasAdaptations && propertyAdaptations && <PropertyAdaptationsCard adaptations={propertyAdaptations} />}
                {renderHazardComponent}
              </Stack>
            </Box>
          </>
        )}
      </Stack>
    </Page>
  )
}

export default PropertyClimateAnalytics
